import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { useAllVisitFiles, useDeleteVisitFile } from '../../hooks/useVisits';
import { PageHeader } from '../../components/common/PageHeader';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { ImageGallery } from '../../components/visits/ImageGallery';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import type { VisitFileDto } from '../../types/api.types';
import { formatDate } from '../../utils/formatters';

export const VisitImagesPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const { patientId } = useParams<{ patientId: string }>();
  const id = patientId ? parseInt(patientId, 10) : 0;

  const { data: allFiles, isLoading, error } = useAllVisitFiles(id);
  const deleteMutation = useDeleteVisitFile(id);
  const [fileIdToDelete, setFileIdToDelete] = useState<number | null>(null);

  // Group files by visit date (we'll need to get visit info, but for now group by uploadedAt)
  const groupedFiles = useMemo(() => {
    if (!allFiles) return new Map<string, VisitFileDto[]>();

    const groups = new Map<string, VisitFileDto[]>();
    allFiles.forEach((file) => {
      const dateKey = formatDate(file.uploadedAt);
      const existing = groups.get(dateKey) || [];
      existing.push(file);
      groups.set(dateKey, existing);
    });

    return groups;
  }, [allFiles]);

  if (isLoading) {
    return <LoadingSpinner size="lg" className="py-12" />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!allFiles || allFiles.length === 0) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'}>
        <PageHeader title={t('visits.visitImages')} />
        <div className="text-center py-12">
          <p className="text-gray-500">{t('visits.noImagesFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader title={t('visits.allVisitImages')} />

      <div className="space-y-8">
        {Array.from(groupedFiles.entries()).map(([date, files]) => (
          <div key={date} className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">{date}</h2>
            <ImageGallery
              images={files}
              onDelete={(fileId) => setFileIdToDelete(fileId)}
            />
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={fileIdToDelete !== null}
        title={t('visits.deleteImage')}
        message={t('visits.deleteImageConfirmation')}
        confirmText={t('common.delete')}
        variant="danger"
        onClose={() => setFileIdToDelete(null)}
        onConfirm={async () => {
          if (!fileIdToDelete) {
            return;
          }
          await deleteMutation.mutateAsync(fileIdToDelete);
          setFileIdToDelete(null);
        }}
      />
    </div>
  );
};

